/**
 * Shared news constants used by the admin panel, public hub, detail page and
 * home-page banner. Categories mirror the Django ``NewsCategory`` choices; each
 * carries a label and an emoji/accent used for the filter tabs and the card
 * category badges.
 *
 * i18n: each definition carries both a ``labelKey`` (resolved via ``t()`` in the
 * translated public components — this is a module constant and can't call hooks)
 * and the original Swedish ``label`` kept for the not-yet-translated admin panel
 * (AdminNews). validateNewsImage uses the i18n singleton for its messages.
 */
import i18n from '../../i18n';

/** Ordered category definitions (value mirrors the API; labelKey/emoji for UI). */
export const NEWS_CATEGORIES = [
  { value: 'update', labelKey: 'news.catUpdate', label: 'Uppdatering', emoji: '🛠️' },
  { value: 'tournament', labelKey: 'news.catTournament', label: 'Turnering', emoji: '🏆' },
  { value: 'league', labelKey: 'news.catLeague', label: 'Liga', emoji: '🥇' },
  { value: 'event', labelKey: 'news.catEvent', label: 'Event', emoji: '📅' },
  { value: 'community', labelKey: 'news.catCommunity', label: 'Community', emoji: '💬' },
  { value: 'other', labelKey: 'news.catOther', label: 'Övrigt', emoji: '📰' },
];

/** Lookup map: category value -> definition. */
export const NEWS_CATEGORY_MAP = NEWS_CATEGORIES.reduce((acc, c) => {
  acc[c.value] = c;
  return acc;
}, {});

/** Post type definitions (on-site markdown article vs. external link). */
export const NEWS_POST_TYPES = [
  { value: 'onsite', labelKey: 'news.postTypeOnsite', label: 'Inlägg på sidan' },
  { value: 'external', labelKey: 'news.postTypeExternal', label: 'Extern länk' },
];

/** Max cover-image upload size in bytes — mirrors the API's 5 MB cap. */
export const NEWS_IMAGE_MAX_BYTES = 5 * 1024 * 1024;

/** Accepted cover-image MIME types — mirrors the API's allowed extensions. */
export const NEWS_IMAGE_ACCEPT = 'image/png,image/jpeg,image/webp,image/gif';

/**
 * Validate a chosen cover-image file against the API's size/type limits so the
 * user gets instant feedback instead of a silently-rejected upload.
 *
 * @param {File} file - The file selected in a file input.
 * @returns {string|null} A translated error message, or null when the file is OK.
 */
export const validateNewsImage = (file) => {
  if (!file) return null;
  if (file.size > NEWS_IMAGE_MAX_BYTES) return i18n.t('news.imageTooBig');
  if (file.type && !NEWS_IMAGE_ACCEPT.split(',').includes(file.type)) {
    return i18n.t('news.imageBadType');
  }
  return null;
};
